import "../../pages/templates/templates.css";
import "./campaign-thumb.css";
import {BarChartOutlined, DeleteOutlined, PoweroffOutlined, SendOutlined} from "@ant-design/icons";
import useAppContext from "../../hooks/useAppContext";
import {Campaign} from "../../types/CampaignProps";
import {Button, Tooltip} from "antd";
import {useEffect, useState} from "react";
import {updateCampaign} from "../../api/updateCampaign";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import {sendManualCampaign} from "../../api/sendManualCampaign";
import CampaignThumbPreview from "./campaignThumbPreview/CampaignThumbPreview";
import CampaignTriggerLabel from "./campaignTriggerLabel/CampaignTriggerLabel";
import CampaignResendLabel from "./campaignResendLabel/CampaignResendLabel";
import CampaignThumbAudience from "./campaignThumbAudience/CampaignThumbAudience";
import {removeCampaign} from "../../api/removeCampaign";
import CampaignInsightsPreviewChart from "./CampaignInsightsPreviewChart";
import CampaignCostLabel from "./campaignCostLabel/CampaignCostLabel";

interface CampaignThumbProps {
    campaign: Campaign;
    onCampaignClick: () => void;
    index: number;
    isDraftView?: boolean;
    onCampaignAnalyticsClick: (campaignId: number) => void;
    totalCampaignsCount: number;
}

const CampaignThumb = ({ campaign, onCampaignClick, index, isDraftView = false, onCampaignAnalyticsClick, totalCampaignsCount }: CampaignThumbProps) => {
    const { data: { shop, templates, campaigns, segments }, actions: { loadCampaigns } } = useAppContext();
    const template = campaign?.templates?.[0];
    const statusLowerCase = template?.whatsappTemplateStatus?.toLowerCase() || '';
    const isApproved = statusLowerCase === "approved";
    const isPending = statusLowerCase === "pending";
    const isTemplateActive = template?.active;
    const isDisabled = !campaign.active || !template?.active;
    const withImage = template?.withImage;
    const whatsappTemplateStatus = template?.whatsappTemplateStatus;
    const content = template?.content;
    const title = campaign?.title;
    const [isSending, setIsSending] = useState(false);
    const { selectedDomain } = useUserContext();
    const { showToast, closeToast } = useToast();
    const [isLoadingActiveToggle, setIsLoadingActiveToggle] = useState(false);
    const [isActive, setIsActive] = useState(campaign.active || false);
    const [membersWithPhoneNumbers, setMembersWithPhoneNumbers] = useState<number | undefined>(undefined);
    const [membersWithEmail, setMembersWithEmail] = useState<number | null>(null);
    const isManual = campaign.triggerType === 'manual';
    const isSendingMode = campaign.state === 'sending';
    const isEditorMode = campaign.state === 'not sent';
    const shouldResend = campaign.totalClientsReceivedCampaign && (campaign.totalClientsReceivedCampaign) < (membersWithPhoneNumbers || 0);
    const isPartiallySent = campaign.totalClientsReceivedCampaign && campaign.totalClientsReceivedCampaign < (membersWithPhoneNumbers || 0)

    const onClick = () => {
        if(isEditorMode) {
            onCampaignClick();
        } else {
            onCampaignAnalyticsClick(campaign.id);
        }
    }

    const toggleActive = async (event: any) => {
        event.stopPropagation();
        if(!selectedDomain || !campaign?.id) return;
        setIsLoadingActiveToggle(true);
        const response = await updateCampaign(selectedDomain, campaign.id, !campaign.active);
        if(response.data === 'success') {
            showToast({ skin: ToastSkin.approval, text: 'The campaign was successfully updated' });
            setIsActive(!isActive);
        } else {
            showToast({ skin: ToastSkin.error, text: 'Failed to update the campaign' });
        }
        setIsLoadingActiveToggle(false);
    }

    const deleteCampaignClick = async (event: any) => {
        event.stopPropagation();
        showToast({ skin: ToastSkin.danger, text: 'Are you sure you want to completely remove this campaign?', buttons: [
                { text: 'Yes', onClick: deleteCampaign },
                { text: 'No, Cancel', onClick: closeToast }
            ] });
    }

    const deleteCampaign = async () => {
        if(selectedDomain && campaign.id) {
            const response = await removeCampaign(selectedDomain, campaign.id);
            if(response.data) {
                loadCampaigns();
                showToast({ skin: ToastSkin.approval, text: 'Campaign was deleted successfully'});
            } else {
                showToast({ skin: ToastSkin.error, text: 'Failed to delete campaign'});
            }
        }
    }

    const sendCampaign = async () => {
        if(selectedDomain && campaign.id && isTemplateActive && isApproved) {
            setIsSending(true);
            try {
                const response = await sendManualCampaign(selectedDomain, campaign.id);
                const isSuccess = response?.data?.success;
                if (isSuccess) {
                    const text = "Campaign started! Messages are now being sent to your clients. This may take a few minutes to complete.";
                    showToast({skin: ToastSkin.approval, text});
                    loadCampaigns();
                } else {
                    showToast({
                        skin: ToastSkin.error,
                        text: "Failed to start campaign. Something went wrong while trying to send your messages. Please try again or contact support."
                    });
                    loadCampaigns();
                }
            } catch(error) {
                showToast({
                    skin: ToastSkin.error,
                    text: "Failed to start campaign. Something went wrong while trying to send your messages. Please try again or contact support."
                });
                loadCampaigns();
            }
            setIsSending(false);
        }
    }

    const onSendManualCampaignClick = async (event: any) => {
        event.stopPropagation();
        showToast({ skin: ToastSkin.neutral, text: 'This campaign will be delivered to your chosen customers. Are you sure you want to continue?', buttons: [
                { text: 'Yes', onClick: sendCampaign},
                { text: 'No, Cancel', onClick: closeToast }
            ]});
    }

    useEffect(() => {
        setIsActive(campaign.active);
        setMembersWithPhoneNumbers(campaign.customersCount || 0);
        setMembersWithEmail(campaign.customersCount || 0);
    }, [campaign, segments.data]);

    return (
        <div className={`campaign-thumb-container ${isDraftView ? 'draft' : ''}`} style={{ zIndex: totalCampaignsCount - index }} onClick={onClick}>
            <CampaignThumbPreview isDisabled={isDisabled} status={statusLowerCase} whatsappTemplateStatus={whatsappTemplateStatus} isActive={isActive} content={content} isPending={isPending} withImage={withImage} isEditorMode={isEditorMode} isPartiallySent={!!isPartiallySent} imageUrl={template?.imageUrl} />
            <div className="campaign-thumb-details">
                <div className="top">
                    <div className="left">
                        <div className="top">
                            <div className="campaign-thumb-title">
                                <span>{title}</span>
                            </div>
                            {isEditorMode ? <CampaignThumbAudience campaign={campaign} isEditorMode={isEditorMode} isSending={isSending} onSendManualCampaignClick={onSendManualCampaignClick} membersWithPhoneNumbers={membersWithPhoneNumbers} /> : null}
                            <CampaignResendLabel campaign={campaign} membersWithPhoneNumbers={campaign.customersCount} onSendManualCampaignClick={onSendManualCampaignClick} isSending={isSending} />
                            {isEditorMode ? <CampaignTriggerLabel campaign={campaign} isManual={isManual} /> : null}
                            <CampaignCostLabel costUsd={campaign.actualCost} estimatedCostUsd={campaign.costEstimationOnSending || campaign.expectedCost} />
                        </div>
                        <div className="bottom">
                            {/*Stats to be added*/}
                        </div>
                    </div>
                    <div className="right">
                        <div className="actions">
                            {isEditorMode ?
                                <>
                                    <Tooltip title={!isManual ? 'Scheduled campaigns are automatically launched at their set time and cannot be triggered manually.' : !membersWithPhoneNumbers || !isApproved || !isActive ? 'The campaign is inactive, the template is disabled, or no members were found in the selected audience.' : ''}>
                                        <Button size="large" loading={isSending} icon={<SendOutlined width={18}/>} disabled={!isManual || !membersWithPhoneNumbers || !isApproved || !isActive} type="primary" className="campaign-action-button send-button" onClick={onSendManualCampaignClick}>Send</Button>
                                    </Tooltip>
                                    <div className="bottom-actions">
                                        <Button loading={isLoadingActiveToggle} disabled={isSending} type="text" onClick={toggleActive} className="campaign-action-button text" icon={<PoweroffOutlined width={14} />}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                                        <Button loading={isLoadingActiveToggle} disabled={isSending} type="text" onClick={deleteCampaignClick} className="campaign-action-button text" icon={<DeleteOutlined width={14} />}>Delete</Button>
                                    </div>
                                </>
                                :
                                <>
                                    <div className="bottom-actions insights">
                                        <Button size="large" loading={isSending} icon={<BarChartOutlined width={18}/>}
                                                type="primary" onClick={onClick}>Campaign Insights</Button>
                                    </div>
                                </>
                            }
                            {/*<ConsentPolicySelector consentPolicy={} setConsentPolicy={}*/}
                        </div>
                    </div>
                </div>
                {isEditorMode ? null :
                    <div className="bottom insights-bar">
                        <div className="chart"><CampaignInsightsPreviewChart targets={campaign.customersCount || 0} deliveries={campaign.insights?.delivered || 0} messagesRead={campaign.insights?.read || 0} uniqueClicks={campaign.insights?.uniqueClicks || 0} conversions={campaign.insights?.orders || 0} /></div>
                        <div className="insight-cell"><div>{campaign.targetsCountOnSending || campaign.customersCount}</div>Targets</div>
                        <div className="insight-cell"><div>{campaign.insights?.delivered}</div>Deliveries</div>
                        <div className="insight-cell"><div>{campaign.insights?.read}</div>Messages Read</div>
                        <div className="insight-cell"><div>{campaign.insights?.uniqueClicks}</div>Unique Clicks</div>
                        <div className="insight-cell"><div>{campaign.insights?.orders}</div>Conversions</div>
                    </div>
                }
            </div>
        </div>
    )
}

export default CampaignThumb;